import { Link } from "react-router-dom";
import { ArrowRight, Briefcase, Heart, MapPin, User, Users } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { IMAGES, SITE } from "../config/site";

const AUDIENCES = [
  { icon: User, label: "Individual Travellers" },
  { icon: Users, label: "Families" },
  { icon: Briefcase, label: "Corporate Clients" },
  { icon: Heart, label: "Travel Agents" },
];

export default function About() {
  return (
    <>
      <section className="relative flex min-h-[55svh] items-end overflow-hidden bg-ocean pt-[72px]">
        <img src={IMAGES.about} alt="Travellers looking out over a tropical coastline"
          className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ocean via-ocean/50 to-ocean/30" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-14 sm:px-6">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-sand">About Us</p>
            <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
              Travel planning with a<br className="hidden sm:block" /> personal touch.
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
        <div className="grid gap-12 lg:grid-cols-2">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-turquoise">Who We Are</p>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">
              A travel agency based in {SITE.city}
            </h2>
            <div className="mt-5 space-y-4 text-base leading-relaxed text-mutedink">
              <p>
                Arora Travel Online (ATO) is a travel agency based in Rohini, New Delhi.
                We provide travel-related services for individuals, families, corporate
                clients and travel agents — from flight bookings and holiday packages
                to cruise bookings, visa assistance and travel insurance guidance.
              </p>
              <p>
                Our focus is simple: understand what you want from your trip, walk you
                through the available options, and help you plan with clarity. Every
                journey starts with a conversation — through our enquiry form or a
                quick message on WhatsApp.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="rounded-3xl bg-sand p-8">
              <h3 className="font-display text-xl font-bold text-ink">Who we work with</h3>
              <div className="mt-6 grid grid-cols-2 gap-4">
                {AUDIENCES.map((a) => (
                  <div key={a.label} className="flex items-center gap-3 rounded-2xl bg-white p-4">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-turquoise/10 text-turquoise">
                      <a.icon className="h-5 w-5" />
                    </span>
                    <span className="text-sm font-semibold text-ink">{a.label}</span>
                  </div>
                ))}
              </div>
              <p className="mt-6 text-sm leading-relaxed text-mutedink">
                Whether it's a family holiday in Kerala, a honeymoon in the Maldives,
                a business trip, or support for your own travel agency — we start with
                your requirements and take it from there.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-sand py-16 sm:py-24">
        <div className="mx-auto grid max-w-7xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-turquoise">Visit Us</p>
            <h2 className="mt-3 font-display text-2xl font-bold tracking-tight text-ink sm:text-3xl">
              Find Our Office in Rohini
            </h2>
            <p className="mt-4 max-w-md text-base leading-relaxed text-mutedink">
              Drop in to discuss your travel plans in person — we're easy to reach.
            </p>
            <p className="mt-6 flex items-start gap-2.5 text-sm font-semibold text-ink">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-coral" />
              {SITE.address}
            </p>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(SITE.address)}`}
              target="_blank" rel="noopener noreferrer"
              data-testid="about-directions-link"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-coral px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#B01A23]"
            >
              Get Directions <ArrowRight className="h-4 w-4" />
            </a>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="overflow-hidden rounded-3xl shadow-lg ring-1 ring-ocean/10">
              <iframe
                title="Arora Travel Online office location — Sector 18, Rohini, Delhi"
                src={`https://maps.google.com/maps?q=${encodeURIComponent(SITE.address)}&output=embed`}
                data-testid="about-office-map"
                className="h-[380px] w-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-ocean py-16 text-center text-white sm:py-20">
        <Reveal className="mx-auto max-w-2xl px-4">
          <h2 className="font-display text-2xl font-bold tracking-tight sm:text-3xl">
            Have a trip in mind?
          </h2>
          <p className="mt-3 text-base text-white/70">
            Share your plans and our team will help you explore the options.
          </p>
          <Link to="/plan-your-trip" data-testid="about-plan-trip-button"
            className="mt-7 inline-flex items-center gap-2 rounded-full bg-coral px-7 py-3.5 text-sm font-semibold text-white shadow-lg shadow-coral/30 transition-all duration-300 hover:-translate-y-0.5 hover:bg-[#B01A23]">
            Plan Your Trip <ArrowRight className="h-4 w-4" />
          </Link>
        </Reveal>
      </section>
    </>
  );
}
